import React, { useState, useRef } from "react";
import { Send, MessageCircle } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

interface QA {
  question: string;
  answer: string;
  items_searched: number;
}

export const FeedChat: React.FC = () => {
  const { session } = useAuth();
  const [question, setQuestion] = useState("");
  const [history, setHistory] = useState<QA[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const ask = async () => {
    const q = question.trim();
    if (!q || !session?.access_token || loading) return;
    setLoading(true);
    setError(null);
    setQuestion("");
    try {
      const { data, error: fnError } = await supabase.functions.invoke("chat-with-feed", {
        body: { question: q },
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      if (fnError) throw fnError;
      if (data?.error) throw new Error(data.error);
      const newEntry: QA = { question: q, answer: data.answer, items_searched: data.items_searched };
      setHistory((prev) => [newEntry, ...prev].slice(0, 5));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to get answer");
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      ask();
    }
  };

  return (
    <Card className="border border-[#E5E7EB] flex flex-col">
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold text-[#1A2E4A] flex items-center gap-2">
          <MessageCircle className="h-4 w-4 text-[#1A2E4A]" />
          Chat with Your Feed
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-3">
        {/* Q&A history */}
        {history.length > 0 && (
          <div className="space-y-3 max-h-64 overflow-y-auto">
            {history.map((qa, i) => (
              <div key={i} className="space-y-1">
                <p className="text-xs font-medium text-[#1A2E4A]">{qa.question}</p>
                <p className="text-sm text-gray-700 leading-relaxed">{qa.answer}</p>
                <p className="text-xs text-muted-foreground">Searched {qa.items_searched} posts</p>
              </div>
            ))}
          </div>
        )}
        {history.length === 0 && !loading && (
          <p className="text-sm text-muted-foreground">
            Ask anything about your feed...
          </p>
        )}
        {loading && (
          <p className="text-sm text-muted-foreground animate-pulse">Searching your feed...</p>
        )}
        {error && <p className="text-sm text-red-500">{error}</p>}
        {/* Input */}
        <div className="flex gap-2 mt-auto">
          <Input
            ref={inputRef}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="What was trending in my rules this week? Did anyone post about AI policy?'"
            disabled={loading}
            className="text-sm"
          />
          <Button size="sm" onClick={ask} disabled={loading || !question.trim()}>
            <Send className="h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default FeedChat;
